import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, Building2, Briefcase, MessageSquare, FileText, Loader2, GraduationCap } from "lucide-react";

export default function AdminDashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin_dashboard_stats"],
    queryFn: async () => {
      const [profiles, orgs, jobs, contacts, applications, enrollments] = await Promise.all([
        supabase.from("profiles").select("id, profile_status", { count: "exact", head: false }).eq("role", "student"),
        supabase.from("partner_orgs").select("id", { count: "exact", head: true }),
        supabase.from("job_posts").select("id, status", { count: "exact", head: false }),
        supabase.from("contact_requests").select("id, status", { count: "exact", head: false }),
        supabase.from("job_applications").select("id", { count: "exact", head: true }),
        supabase.from("course_applications").select("id, status", { count: "exact", head: false }),
      ]);

      const studentData = profiles.data ?? [];
      const jobData = jobs.data ?? [];
      const contactData = contacts.data ?? [];

      return {
        totalStudents: studentData.length,
        readyStudents: studentData.filter((p) => p.profile_status === "ready").length,
        totalPartners: orgs.count ?? 0,
        totalJobs: jobData.length,
        publishedJobs: jobData.filter((j) => j.status === "publicada").length,
        inReviewJobs: jobData.filter((j) => j.status === "em_revisao").length,
        pendingContacts: contactData.filter((c) => c.status === "requested").length,
        totalApplications: applications.count ?? 0,
        pendingEnrollments: (enrollments.data ?? []).filter(e => e.status === "applied").length,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const cards = [
    { label: "Alunos cadastrados", value: stats?.totalStudents ?? 0, sub: `${stats?.readyStudents ?? 0} aptos`, icon: Users, color: "text-primary" },
    { label: "Parceiros ativos", value: stats?.totalPartners ?? 0, icon: Building2, color: "text-accent" },
    { label: "Vagas publicadas", value: stats?.publishedJobs ?? 0, sub: `${stats?.inReviewJobs ?? 0} em revisão`, icon: Briefcase, color: "text-success" },
    { label: "Contatos pendentes", value: stats?.pendingContacts ?? 0, icon: MessageSquare, color: "text-warning" },
    { label: "Candidaturas", value: stats?.totalApplications ?? 0, icon: FileText, color: "text-muted-foreground" },
    { label: "Matrículas pendentes", value: stats?.pendingEnrollments ?? 0, icon: GraduationCap, color: "text-accent" },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold text-foreground">Dashboard Admin</h1>
        <p className="text-muted-foreground text-sm mt-1">Visão geral do CorreTalent OS</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
            className="bg-card rounded-2xl p-5 border border-border"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <c.icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <p className="text-sm text-muted-foreground">{c.label}</p>
            </div>
            <p className="text-3xl font-bold text-foreground">{c.value}</p>
            {c.sub && <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
